import React, { useState, useEffect } from 'react';
import { useFamilyAuth } from '@/contexts/FamilyAuthContext';
import axiosClient from '@/api/axiosClient';

/**
 * Phase 15.5: Enhanced Reminder Form Modal
 * Create & Edit reminders with improved validation and UX
 */
export default function ReminderFormModal({ seniorId, reminder, onClose, onSaved }) {
  const { getAuthHeader } = useFamilyAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    reminder_type: 'other',
    repeat: 'none',
    priority: 'normal'
  });

  // Initialize form with reminder data if editing
  useEffect(() => {
    if (reminder) {
      const scheduledTime = new Date(reminder.scheduled_time);
      const year = scheduledTime.getFullYear();
      const month = String(scheduledTime.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledTime.getDate()).padStart(2, '0');
      const hours = String(scheduledTime.getHours()).padStart(2, '0');
      const minutes = String(scheduledTime.getMinutes()).padStart(2, '0');
      
      setFormData({
        title: reminder.title || '',
        description: reminder.description || '',
        date: `${year}-${month}-${day}`,
        time: `${hours}:${minutes}`,
        reminder_type: reminder.reminder_type || 'other',
        repeat: reminder.repeat || 'none',
        priority: reminder.priority || 'normal'
      });
    }
  }, [reminder]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.title.trim()) {
      setError('Bitte geben Sie einen Titel ein.');
      return;
    }
    if (!formData.date || !formData.time) {
      setError('Bitte geben Sie Datum und Uhrzeit ein.');
      return;
    }

    // Combine date and time into ISO string
    const scheduledDateTime = new Date(`${formData.date}T${formData.time}`);
    
    // Check if date is in the past (warning, but allow)
    const now = new Date();
    if (scheduledDateTime < now) {
      const confirmed = window.confirm('Der gewählte Zeitpunkt liegt in der Vergangenheit. Möchten Sie fortfahren?');
      if (!confirmed) return;
    }

    setLoading(true);

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        scheduled_time: scheduledDateTime.toISOString(),
        reminder_type: formData.reminder_type,
        repeat: formData.repeat,
        priority: formData.priority
      };

      if (reminder) {
        // Update existing reminder
        await axiosClient.put(
          `/api/family/reminders/${reminder.id}`,
          payload
        );
      } else {
        // Create new reminder
        await axiosClient.post(
          `/api/family/seniors/${seniorId}/reminders`,
          payload
        );
      }

      onSaved();
    } catch (err) {
      console.error('Error saving reminder:', err);
      if (err.response?.status === 403) {
        setError('Sie haben keine Berechtigung, Erinnerungen zu bearbeiten.');
      } else {
        setError(err.response?.data?.detail || 'Fehler beim Speichern der Erinnerung.');
      }
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'medication', label: 'Medikament', icon: '💊' },
    { value: 'appointment', label: 'Arzttermin', icon: '🏥' },
    { value: 'checkin', label: 'Check-in', icon: '✅' },
    { value: 'other', label: 'Sonstiges', icon: '📌' }
  ];

  const repeatOptions = [
    { value: 'none', label: 'Einmalig' },
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'monthly', label: 'Monatlich' }
  ];

  const priorityOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'Wichtig ⚠️' }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop - Hayat Dark Theme - FIX: z-[100] to be above navigation (z-50) */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal - Hayat Brand Style with Glassmorphism */}
      <div className="relative bg-neutral-900/90 backdrop-blur-xl rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in border border-neutral-600/40">
        {/* Header - Sticky with Glassmorphism */}
        <div className="sticky top-0 bg-neutral-900/95 backdrop-blur-xl border-b border-neutral-600/40 px-6 md:px-8 py-6 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-[#F9FAFB]">
              {reminder ? '✏️ Erinnerung bearbeiten' : '➕ Neue Erinnerung'}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors flex items-center justify-center text-[#CBD5F5] hover:text-[#F9FAFB]"
              aria-label="Schließen"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* Form - Hayat Dark Theme */}
        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] px-4 py-3 rounded-xl animate-fade-in">
              <span className="font-semibold">⚠️ {error}</span>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Titel *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="z.B. Medikament einnehmen"
              className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] placeholder-neutral-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
              disabled={loading}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Beschreibung (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Zusätzliche Details..."
              rows={3}
              className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] placeholder-neutral-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all resize-none"
              disabled={loading}
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                Datum *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                Uhrzeit *
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
                className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
                disabled={loading}
                required
              />
            </div>
          </div>

          {/* Type - Hayat Brand Colors */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Typ
            </label>
            <div className="grid grid-cols-2 gap-3">
              {typeOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('reminder_type', option.value)}
                  className={`
                    px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-300
                    flex items-center justify-center gap-2 min-h-[44px]
                    ${formData.reminder_type === option.value
                      ? 'bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white shadow-lg scale-105'
                      : 'bg-neutral-800/50 text-[#CBD5F5] hover:bg-neutral-800 border border-neutral-600/40 hover:border-[#5B4B8A]/50'
                    }
                  `}
                  disabled={loading}
                >
                  <span className="text-lg">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Repeat */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Wiederholung
            </label>
            <select
              value={formData.repeat}
              onChange={(e) => handleChange('repeat', e.target.value)}
              className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
              disabled={loading}
            >
              {repeatOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority - Hayat Accent Gold */}
          <div>
            <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
              Priorität
            </label>
            <div className="grid grid-cols-2 gap-3">
              {priorityOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('priority', option.value)}
                  className={`
                    px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-300 min-h-[44px]
                    ${formData.priority === option.value
                      ? 'bg-gradient-to-r from-[#F6B352] to-[#d49640] text-white shadow-lg scale-105'
                      : 'bg-neutral-800/50 text-[#CBD5F5] hover:bg-neutral-800 border border-neutral-600/40 hover:border-[#F6B352]/50'
                    }
                  `}
                  disabled={loading}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Footer Actions - Following hayat_layout_system.json modal rules */}
          {/* Clear separation from body, min 44px height, responsive layout */}
          <div className="pt-6 border-t border-neutral-600/40">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Secondary Button - Cancel */}
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:flex-1 px-6 py-4 bg-neutral-800/80 backdrop-blur-sm text-[#F9FAFB] rounded-xl hover:bg-neutral-800 transition-all font-semibold border-2 border-[#6DCEE8] hover:bg-[#6DCEE8]/10 hover:scale-105 active:scale-95 min-h-[44px] order-2 sm:order-1"
                disabled={loading}
              >
                Abbrechen
              </button>
              {/* Primary Button - Save (Hayat Violett) */}
              <button
                type="submit"
                className="w-full sm:flex-1 px-6 py-4 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-xl hover:shadow-xl transition-all font-semibold hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] order-1 sm:order-2"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Wird gespeichert...
                  </span>
                ) : (
                  reminder ? '💾 Aktualisieren' : '✅ Erstellen'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
